import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, CheckCircle2, Clock, ArrowRight } from 'lucide-react';
import { apiClient } from '../../services/api';

const api = {
  runs: () => apiClient.get('/payroll/runs').then((r) => r.data),
  payslips: (id) => apiClient.get(`/payroll/runs/${id}/payslips`).then((r) => r.data),
};

const PayrollStatusPage = () => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.runs(); setRuns(r.data || []); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const posted = runs.filter((r) => r.accounts_status === 'posted');
  const pending = runs.filter((r) => r.handed_off && r.accounts_status !== 'posted');
  const not_handed = runs.filter((r) => r.status === 'executed' && !r.handed_off);
  const draft = runs.filter((r) => r.status !== 'executed');
  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="mb-4 sm:mb-6">
        <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">Payroll Follow-up</span>
        <h1 className="mt-2 text-3xl sm:text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>Status of my submissions</h1>
        <p className="mt-1 text-xs sm:text-sm text-[#0b1a2e]/60">Read-only view of every payroll run — who has posted it to Books ledger and when.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatusTile icon={<Clock className="w-5 h-5" />} label="Draft / not executed" v={draft.length} color="text-slate-500" />
        <StatusTile icon={<ArrowRight className="w-5 h-5" />} label="Executed, not handed off" v={not_handed.length} color="text-amber-600" />
        <StatusTile icon={<Clock className="w-5 h-5" />} label="Handed off, pending post" v={pending.length} color="text-blue-700" />
        <StatusTile icon={<CheckCircle2 className="w-5 h-5" />} label="Posted to Books" v={posted.length} color="text-emerald-700" />
      </div>
      {loading ? <div className="p-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#0f5c3d] inline" /></div> : (
        <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#f7fbf8]"><tr>
              <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Period</th>
              <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Country</th>
              <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Executed</th>
              <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Handed off</th>
              <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Posted</th>
              <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Overall status</th>
            </tr></thead>
            <tbody>{runs.map((r) => (
              <tr key={r.id} className="border-t border-black/5" data-testid={`status-row-${r.id}`}>
                <td className="p-2 font-mono text-xs">{r.period_year}-{String(r.period_month).padStart(2, '0')}</td>
                <td className="p-2 text-xs">{r.country || 'ALL'}</td>
                <td className="p-2 text-xs">{r.executed_at ? new Date(r.executed_at).toLocaleDateString() : '—'}</td>
                <td className="p-2 text-xs">{r.handed_off_at ? new Date(r.handed_off_at).toLocaleDateString() : <span className="text-[#0b1a2e]/40">—</span>}</td>
                <td className="p-2 text-xs">{r.posted_at ? new Date(r.posted_at).toLocaleDateString() : <span className="text-[#0b1a2e]/40">—</span>}</td>
                <td className="p-2">
                  <StatusBadge run={r} />
                </td>
              </tr>
            ))}</tbody>
          </table>
          {runs.length === 0 && <div className="p-8 text-center text-sm text-[#0b1a2e]/50">No runs yet.</div>}
        </div>
      )}
    </div>
  );
};

const StatusBadge = ({ run }) => {
  if (run.accounts_status === 'posted') return <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">Posted to Books</span>;
  if (run.handed_off) return <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">Pending post</span>;
  if (run.status === 'executed') return <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">Not handed off</span>;
  return <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">Draft</span>;
};

const StatusTile = ({ icon, label, v, color }) => (
  <div className="bg-white border border-black/5 rounded-2xl p-4">
    <div className={`${color}`}>{icon}</div>
    <div className={`text-2xl font-bold ${color} mt-1`}>{v}</div>
    <div className="text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">{label}</div>
  </div>
);

export default PayrollStatusPage;
